import React, { useState, useEffect, useRef } from "react";
import styles from "./MenuComponent.module.css";
import { gsap } from "gsap";

// Import menu data
import hamiMenuData from "./data/Hami_menu.json";
import menu16Data from "./data/menu16.json";

const MenuComponent = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const menuRefs = useRef([]);
  const filterRef = useRef(null);
  const containerRef = useRef(null);

  // Combine all menu data (normalized from Hami_menu.json's schema)
  const allMenuData = hamiMenuData.categories.map((category) => ({
    key: category.title,
    name: category.title,
    meatOptions: category.meat_options,
    items: category.foods.map((food) => ({
      name: food.name,
      description: food.des,
      price: food.price,
      prices: food.prices,
      pcs: food.pcs,
    })),
  }));

  // Get unique categories for filter
  const categoryOptions = [
    { key: "all", name: "Alle Speisen" },
    ...allMenuData.map((category) => ({
      key: category.key || category.name,
      name: category.name,
    })),
  ];

  // Filter categories based on selection
  const filteredCategories = selectedCategory === "all" 
    ? allMenuData 
    : allMenuData.filter((category) => 
        (category.key || category.name) === selectedCategory
      );

  // GSAP Animation for categories on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            
            // Animate category with GSAP
            gsap.fromTo(
              entry.target,
              {
                opacity: 0,
                y: 50,
                scale: 0.95,
              },
              {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 0.8,
                ease: "power3.out",
                delay: index * 0.1,
              }
            );
            
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    const currentRefs = menuRefs.current;
    currentRefs.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      currentRefs.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [filteredCategories]);

  // Animate filter on mount
  useEffect(() => {
    gsap.fromTo(
      filterRef.current,
      {
        opacity: 0,
        y: -30,
      },
      {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power2.out",
      }
    );
  }, []);

  // Handle category filter change
  const handleCategoryChange = (categoryKey) => {
    setSelectedCategory(categoryKey);
    
    // Reset scroll to top
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  // Render individual menu item
  const renderMenuItem = (item, itemIndex) => {
    return (
      <div key={itemIndex} className={styles.menuItem}>
        <div className={styles.itemHeader}>
          <h4 className={styles.itemName}>
            {item.name}
            {item.pcs && ` (${item.pcs} pcs)`}
          </h4>
          {item.price != null && (
            <span className={styles.price}>€{item.price.toFixed(2)}</span>
          )}
        </div>
        {item.description && (
          <p className={styles.itemDescription}>{item.description}</p>
        )}
        {item.prices && (
          <div className={styles.priceOptions}>
            {Object.entries(item.prices).map(([option, price]) => (
              <div key={option} className={styles.priceOption}>
                <span className={styles.optionName}>{option}:</span>
                <span className={styles.optionPrice}>€{price.toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render menu category
  const renderCategory = (category, index) => {
    return (
      <div 
        key={category.key || index} 
        className={styles.categoryCard}
        ref={(el) => (menuRefs.current[index] = el)}
        data-index={index}
        style={{ opacity: 0 }}
      >
        <h2 className={styles.categoryTitle}>{category.name}</h2>

        {category.instruction && (
          <p className={styles.categoryInstruction}>{category.instruction}</p>
        )}

        {category.description && (
          <p className={styles.categoryDescription}>{category.description}</p>
        )}

        <div className={styles.itemsGrid}>
          {category.items?.map((item, itemIndex) =>
            renderMenuItem(item, itemIndex),
          )}
        </div>

        {category.meatOptions && (
          <div className={styles.priceOptions}>
            {category.meatOptions.map((option) => (
              <div key={option.code} className={styles.priceOption}>
                <span className={styles.optionName}>
                  {option.code}. {option.name}
                  {option.name_en ? ` (${option.name_en})` : ""}:
                </span>
                <span className={styles.optionPrice}>
                  €{option.price.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render allergen legend
  const renderAllergenLegend = () => {
    return (
      <div className={styles.allergenCard}>
        <h2 className={styles.categoryTitle}>Allergene</h2>
        <div className={styles.allergenGrid}>
          {Object.entries(menu16Data.allergene_legend).map(
            ([code, description]) => (
              <div key={code} className={styles.allergenItem}>
                <span className={styles.allergenCode}>{code}:</span>
                <span className={styles.allergenDescription}>
                  {description}
                </span>
              </div>
            ),
          )}
        </div>
      </div>
    );
  };
  
  // Card view for both desktop and mobile
  const renderCardView = () => {
    return (
      <div className={styles.cardView} ref={containerRef}>
      

        {/* Menu Categories */}
        <div className={styles.categoriesContainer}>
          {filteredCategories.map((menuData, menuIndex) => (
            <div key={menuIndex} className={styles.pageMenu}>
              {renderCategory(menuData, menuIndex)}
            </div>
          ))}
        </div>

        {/* Allergen Legend */}
        {renderAllergenLegend()}
      </div>
    );
  };

  return <div className={styles.menuContainer}>
       {/* Sticky Filter */}
        <div className={styles.stickyFilter} ref={filterRef}>
          <div className={styles.filterContainer}>
            <h3 className={styles.filterTitle}>Kategorien</h3>
            <div className={styles.filterButtons}>
              {categoryOptions.map((option) => (
                <button
                  key={option.key}
                  className={`${styles.filterButton} ${
                    selectedCategory === option.key ? styles.active : ""
                  }`}
                  onClick={() => handleCategoryChange(option.key)}
                >
                  {option.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* PDF Menu Button */}
        <a 
          href="/assets/images/menu/Hami-Menu.pdf" 
          target="_blank" 
          rel="noopener noreferrer"
          className={styles.pdfMenuButton}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14,2 14,8 20,8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10,9 9,9 8,9"></polyline>
          </svg>
          <span>Menu PDF</span>
        </a>
        
    {renderCardView()}
    </div>;
};

export default MenuComponent;
